import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import React, { useImperativeHandle } from 'react';
import { store, useSelector } from 'store';
import NavItem from '../NavItem';
import { NavItemType } from 'types';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CustomView } from 'types/userConfig/userConfig';
import { clickPreventDefaultAndStopPropagation } from 'utils/helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseCustomChatViewIdFromPathname } from 'utils/chat-path-params';
import CreateCustomView, { CreateCustomViewRefProps } from './CreateCustomView';
import { enqueueSnackbar } from 'notistack';
import { dispatch } from 'store';
import { updateUserConfig } from 'store/slices/userConfig';
import { deleteCompanyCustomView, extractCustomViewsError } from 'api/custom-views';
import { IconMessageCode } from '@tabler/icons-react';
import { useGetMenuMaster } from 'api/menu';
import useConfig from 'hooks/useConfig';
import { useConfirmation } from 'hooks/useConfirmation';
import ConfirmationDialog from 'ui-component/system/ConfirmationDialog';
import CustomViewVisibilityDialog from './CustomViewVisibilityDialog';

interface AdditionalViewsProps {
  createEditView: 'create' | 'edit' | null;
  setCreateEditView: React.Dispatch<React.SetStateAction<'create' | 'edit' | null>>;
  /** טאבים מותאמים מוצגים בשורת הטאבים — מסתיר את הרשימה */
  suppressInlineList?: boolean;
}

export type AdditionalViewsHandle = {
  /** פתיחת דיאלוג יצירת תצוגה. folderId — התיקייה שאליה תשויך התצוגה החדשה (אחסון מקומי). */
  openCreate: (folderId?: string) => void;
  openEdit: (viewId: string) => void;
  deleteView: (viewId: string) => void;
  /** דיאלוג שיתוף/נראות פר-משתמש (אדמין) — נפתח בדאבל-קליק על אייקון התצוגה בסיידבר */
  openVisibility: (viewId: string) => void;
};

const AdditionalViews = React.forwardRef<AdditionalViewsHandle, AdditionalViewsProps>(function AdditionalViews(
  { createEditView, setCreateEditView, suppressInlineList = false },
  ref,
) {
  const additionalViews = useSelector((state) => state.userConfig.customViews);
  const applicationUsers = useSelector((state) => state.applicationUsers);
  const currentUser = useSelector((state) => state.user);
  const selectedChatUser = useSelector((state) => state.chat.selectedChatUser);
  const location = useLocation();
  const viewId = React.useMemo(() => parseCustomChatViewIdFromPathname(location.pathname), [location.pathname]);
  const navigate = useNavigate();
  const { themeDirection } = useConfig();

  const canManageViewVisibility = currentUser?.Role === 'admin';

  // עריכה/מחיקה — ליוצר התצוגה או לאדמין בלבד (נאכף גם בשרת); תצוגה היסטורית
  // בלי ownerUdid מתנהגת כפתוחה כדי לא לנעול תצוגות שמוגרו בלי בעלים
  const canManageView = React.useCallback(
    (view: CustomView) =>
      currentUser?.Role === 'admin' || !view.ownerUdid || view.ownerUdid === currentUser?.Udid,
    [currentUser?.Role, currentUser?.Udid],
  );

  const [visibilityDialogViewId, setVisibilityDialogViewId] = React.useState<string | number | null>(null);
  const visibilityDialogView = React.useMemo(() => {
    if (visibilityDialogViewId == null) return null;
    return additionalViews.find((v) => v.id === visibilityDialogViewId) ?? null;
  }, [additionalViews, visibilityDialogViewId]);

  const visibleCustomViews = React.useMemo(() => {
    const me = currentUser?.Udid;
    if (!me) return additionalViews;
    return additionalViews.filter((v) => !v.hiddenForApplicationUserUdids?.includes(me));
  }, [additionalViews, currentUser?.Udid]);

  const { open, params, show, confirm, cancel } = useConfirmation({
    onConfirm: () => {
      deleteExistingView(itemToDeleteRef.current!);
    },
    onCancel: () => {
      itemToDeleteRef.current = null;
    },
  });

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const itemToDeleteRef = React.useRef<CustomView | null>(null);

  const createEditFiltersRef = React.useRef<CreateCustomViewRefProps>(null);

  // התיקייה שאליה תשויך תצוגה חדשה (נקבע ביצירה מתוך כותרת תיקייה). אחסון מקומי בלבד — TODO(DB).
  const [targetFolderId, setTargetFolderId] = React.useState<string | undefined>(undefined);

  useImperativeHandle(
    ref,
    () => ({
      openCreate: (folderId?: string) => {
        setTargetFolderId(folderId);
        setCreateEditView('create');
      },
      openEdit: (viewId: string) => {
        const v = store.getState().userConfig.customViews.find((x) => x.id.toString() === viewId);
        if (!v || !canManageView(v)) return;
        createEditFiltersRef.current?.setData(v.id, v.name, v.filters);
        setCreateEditView('edit');
      },
      deleteView: (viewId: string) => {
        const v = store.getState().userConfig.customViews.find((x) => x.id.toString() === viewId);
        if (!v || !canManageView(v)) return;
        itemToDeleteRef.current = v;
        show('מחיקת תצוגה', `האם אתה בטוח שברצונך למחוק את התצוגה "${v.name}"?`);
      },
      openVisibility: (viewId: string) => {
        if (!canManageViewVisibility) return;
        const v = store.getState().userConfig.customViews.find((x) => x.id.toString() === viewId);
        if (!v) return;
        setVisibilityDialogViewId(v.id);
      },
    }),
    [setCreateEditView, show, canManageView, canManageViewVisibility],
  );

  const editExistingFilter = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, view: CustomView) => {
      clickPreventDefaultAndStopPropagation(e);

      createEditFiltersRef.current?.setData(view.id, view.name, view.filters);
      setCreateEditView('edit');
    },
    [setCreateEditView],
  );
  // מחיקה בטבלת CompanyCustomViews (בעלים או אדמין — נאכף גם בשרת)
  const deleteExistingView = React.useCallback(
    async (view: CustomView) => {
      const storeState = store.getState();

      if (viewId === view.id.toString())
        navigate(`/chats/${storeState.chat.selectedChatUser?.UserDisplayPhoneNumber || ''}`);

      try {
        await deleteCompanyCustomView(String(view.id));
        const newViews = storeState.userConfig.customViews.filter((customView) => customView.id !== view.id);
        dispatch(updateUserConfig({ customViews: newViews }));
        enqueueSnackbar(`התצוגה "${view.name}" נמחקה בהצלחה`, { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(extractCustomViewsError(error, 'שגיאה במחיקת התצוגה'), { variant: 'error' });
      }
    },
    [navigate, viewId],
  );

  return (
    <React.Fragment>
      {!suppressInlineList &&
        visibleCustomViews.map((view) => {
        const item: NavItemType = {
          id: view.id.toString(),
          title: view.name,
          type: 'item',
          noTranslation: true,
          url: `/chats/custom/${view.id}/${selectedChatUser?.UserDisplayPhoneNumber || ''}`,
          breadcrumbs: false,
          onViewIconDoubleClick: canManageViewVisibility
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                setVisibilityDialogViewId(view.id);
              }
            : undefined,
          action: drawerOpen && canManageView(view) ? (
            <Stack direction="row" spacing={0.5} sx={{ ml: 1, mr: -0.5 }}>
              <IconButton size="small" onClick={(e) => editExistingFilter(e, view)}>
                <EditOutlinedIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  clickPreventDefaultAndStopPropagation(e);
                  itemToDeleteRef.current = view;
                  show('מחיקת תצוגה', `האם אתה בטוח שברצונך למחוק את התצוגה "${view.name}"?`);
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          ) : undefined,
          actionHoverOnly: true,
          icon: IconMessageCode,
        };
        return (
          <Tooltip
            title={item.title}
            key={view.id}
            placement={themeDirection === 'rtl' ? 'left' : 'right'}
            arrow
          >
            <Box width="100%">
              <NavItem item={item} level={1} />
            </Box>
          </Tooltip>
        );
      })}

      <CreateCustomView
        open={Boolean(createEditView)}
        onClose={() => {
          setCreateEditView(null);
          setTargetFolderId(undefined);
        }}
        createOrEdit={createEditView}
        targetFolderId={targetFolderId}
        ref={createEditFiltersRef}
      />

      <ConfirmationDialog
        open={open}
        title={params?.title || ''}
        message={params?.message || ''}
        onConfirm={confirm}
        onCancel={cancel}
        onClose={cancel}
      />

      <CustomViewVisibilityDialog
        open={Boolean(visibilityDialogView)}
        onClose={() => setVisibilityDialogViewId(null)}
        view={visibilityDialogView}
        applicationUsers={applicationUsers}
      />
    </React.Fragment>
  );
});

export default AdditionalViews;
